import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'outline' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  full?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, Props>(function Button(
  { className, variant = 'primary', size = 'md', full, iconLeft, iconRight, loading, children, disabled, ...rest },
  ref,
) {
  const base = 'inline-flex items-center justify-center gap-2 rounded-md font-sans font-medium transition-[transform,opacity,background] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed select-none';
  const sizes = { sm: 'h-9 px-3 text-[13px]', md: 'h-11 px-4 text-[15px]', lg: 'h-12 px-5 text-[15px]' }[size];
  const variants = {
    primary: 'bg-terre text-paper-elev hover:opacity-95',
    secondary: 'bg-paper-elev text-ink-1 ring-1 ring-paper-edge hover:bg-paper-rec',
    ghost: 'bg-transparent text-ink-1 hover:bg-paper-rec',
    outline: 'bg-transparent text-ink-1 ring-1 ring-paper-edge hover:bg-paper-rec',
    danger: 'bg-danger text-paper-elev hover:opacity-95',
  }[variant];
  return (
    <button ref={ref} disabled={disabled || loading} className={cn(base, sizes, variants, full && 'w-full', className)} {...rest}>
      {iconLeft}
      <span>{loading ? '…' : children}</span>
      {iconRight}
    </button>
  );
});
